/**
 * @license
 * @copyright Copyright Motili Inc., 2019 All Rights Reserved
 */

import _ from 'lodash';
import moment from 'moment-timezone';
import * as ApiService from 'common/services/ApiService';
import { find as findProperties } from 'common/services/PropertyService';
import { find as findProducts } from 'common/services/ProductService';
import { createLoopbackFilter } from 'common/components/data-grid/DataGridUtils';
import { getDistributionCenter } from './AdminDistributionCentersService';

const baseUrl = 'templates';

export const propertyFilter = {
    include: [
        {
            relation: 'client',
            scope: {
                fields: ['id', 'name', 'doingBusinessAs', 'statusId'],
            },
        },
        {
            relation: 'primaryPreferredContractor',
            scope: {
                fields: ['id', 'name'],
            },
        },
        {
            relation: 'secondaryPreferredContractor',
            scope: {
                fields: ['id', 'name'],
            },
        },
    ],
    fields: [
        'id',
        'typeId',
        'clientId',
        'name',
        'primaryPreferredContractorId',
        'secondaryPreferredContractorId',
        'rawAddress',
        'stdAddress',
        'stdDeliveryLine',
        'stdCity',
        'stdRegion',
        'stdPostalCode',
        'standardized',
        'latitude',
        'longitude',
        'statusId',
        'occupied',
    ],
};

/**
 * Process filter
 * @param {*} activeFilters
 * @param {*} activeQuery
 * @param {*} templateType
 * @return {object} filter
 */
export function processFilters(activeFilters, activeQuery, templateType) {
    const query = _.merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    activeFilters.forEach(filter => {
        switch (filter.target) {
            // Search Filters
            case 'NAME':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;
            case 'ID':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;
            case 'CLIENT':
                query.where.and.push({
                    'template.clientId': { inq: filter.term },
                });
                break;
            case 'PROPERTY':
                query.where.and.push({
                    'template.propertyId': { inq: filter.term },
                });
                break;

            // Advanced Filters
            case 'STATUS':
                query.where.and.push({ active: filter.term });
                break;
            case 'CREATED_BEFORE':
                query.where.and.push({
                    created: {
                        lt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'CREATED_AFTER':
                query.where.and.push({
                    created: {
                        gt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'UPDATED_BEFORE':
                query.where.and.push({
                    updated: {
                        lt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'UPDATED_AFTER':
                query.where.and.push({
                    updated: {
                        gt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;

            case 'operationInCountry':
                if (
                    ['CLIENT_TEMPLATE', 'QUOTE_TEMPLATE'].includes(templateType)
                ) {
                    query.where.and.push({ operationInCountry: filter.term });
                }
                break;
            default:
        }
    });

    query.where.and.push({ typeId: templateType });

    return query;
}

/**
 * Get templates
 * @param {object} query
 * @returns {Promise<Array>}
 */
export async function getTemplates(query) {
    if (query) {
        const queryTemplates = await ApiService.post(
            true,
            `${baseUrl}/search`,
            {
                filter: query,
            }
        );

        return mapProperty(queryTemplates);
    }
    return [];
}

export function getActiveQuoteTemplate(operationInCountry = undefined) {
    return getTemplates({
        where: {
            typeId: 'QUOTE_TEMPLATE',
            active: true,
            operationInCountry,
        },
        order: ['name ASC', 'updated DESC'],
    });
}

/**
 * Map property for quote Template
 * @param {array|object} templates
 * @returns {Promise<array|object>}
 */
export async function mapProperty(templates) {
    let _templates = _.isArray(templates) ? templates : [templates];
    const propertyIds = _.reduce(
        _templates,
        (ids, t) => {
            if (
                t.typeId === 'QUOTE_TEMPLATE' &&
                t.template &&
                t.template.propertyId
            ) {
                ids.push(t.template.propertyId);
            }
            return ids;
        },
        []
    );
    if (propertyIds.length > 0) {
        const properties = await findProperties({
            filter: {
                where: {
                    id: {
                        inq: propertyIds,
                    },
                },
                ...propertyFilter,
            },
        });
        const propertyById = _.keyBy(properties, 'id');
        _templates = _.map(_templates, t => {
            if (
                t.typeId === 'QUOTE_TEMPLATE' &&
                t.template &&
                t.template.propertyId
            ) {
                return {
                    ...t,
                    template: {
                        ...t.template,
                        property: propertyById[t.template.propertyId],
                    },
                };
            }
            return t;
        });
    }
    return _.isArray(templates) ? _templates : _templates[0];
}

export async function mapProduct(template) {
    if (template.typeId !== 'QUOTE_TEMPLATE') {
        return template;
    }
    const scope =
        template && template.template && template.template.clientId
            ? { scope: { clientId: template.template.clientId } }
            : undefined;
    const skus = _.map(template.template.lineItems, item => item.sku);
    if (skus.length > 0) {
        const products = await findProducts(
            {
                filter: {
                    where: {
                        sku: {
                            inq: _.uniq(skus),
                        },
                    },
                    include: [
                        {
                            relation: 'bundleProducts',
                            scope: {
                                include: ['product'],
                            },
                        },
                    ],
                },
            },
            scope
        );
        const productBySku = _.keyBy(products, 'sku');
        const _lineItems = _.map(template.template.lineItems, lineItem => {
            const _product = productBySku[lineItem.sku];
            return {
                ...lineItem,
                product: _product || { active: false },
            };
        });
        return {
            ...template,
            template: {
                ...template.template,
                lineItems: _lineItems,
            },
        };
    }
    return template;
}

export async function mapDcVendor(template) {
    if (
        template.typeId !== 'QUOTE_TEMPLATE' ||
        (template.template && !template.template.fulfillmentDetails)
    ) {
        return template;
    }
    const fulfillmentDetails = template.template.fulfillmentDetails;
    const query = {
        filter: {
            include: [
                {
                    relation: 'vendor',
                },
            ],
        },
    };
    const dc = await getDistributionCenter(
        fulfillmentDetails.distributionCenterId,
        query
    );
    return {
        ...template,
        template: {
            ...template.template,
            vendor: dc.vendor,
            distributionCenter: dc,
        },
    };
}

/**
 * Get templates count
 * @param {object} query
 * @returns {Promise<number>}
 */
export async function getCountOfTemplate(query) {
    if (query?.where) {
        return ApiService.post(true, `${baseUrl}/search/count`, {
            where: query.where,
        }).then(result => result.count);
    }
    return 0;
}

/**
 * Create template
 * @param {object} data
 * @returns {Promise<object>}
 */
export async function create(data) {
    return ApiService.post(true, `${baseUrl}`, data);
}

/**
 * Get Template
 * @param {number|string} id
 * @param {object} filter
 * @returns {Promise<object>}
 */
export async function getTemplate(id, filter) {
    return ApiService.get(true, `${baseUrl}/${id}`, { filter });
}

/**
 * Update template
 * @param {number} id
 * @param {object} data
 * @returns {Promise<object>}
 */
export async function update(id, data) {
    return ApiService.patch(true, `${baseUrl}/${id}`, data);
}

/**
 * Clone template
 * @param {*} id
 * @param {String} name
 * @return {Promise}
 */
export async function clone(id, name) {
    const params = `name=${name}`;
    return ApiService.post(true, `${baseUrl}/${id}/clone?${params}`, undefined);
}

/**
 * performs export action on template
 * @param {string} type
 * @param {object} filter
 * @returns {Promise<Object>}
 */
export function exportCsv(type, filter) {
    // eslint-disable-line no-shadow
    const query = {
        exportTypeId: 'EXCEL',
        filter,
        type,
    };
    return ApiService.post(
        true,
        `${baseUrl}/export`,
        { download: true },
        query
    );
}

export function calculateLineItemCost(id, data) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/calculateLineItemCost`,
        data
    );
}

export function getLineItemSummary(templateId) {
    return ApiService.get(true, `${baseUrl}/${templateId}/lineItemSummary`);
}
